import { assertLexiconSafe } from "@talent/protocol";

/**
 * Every Persian string the product shows a human, in one place.
 *
 * Two reasons it is centralised rather than living next to the components that use it.
 *
 * 1. **It is checkable.** The lexicon's prohibitions (§D) apply to what the product
 *    shows a parent, a child and a teacher — not only to what the claim renderer emits.
 *    Copy scattered across components is copy nobody runs a guard over. Every leaf below
 *    goes through `assertLexiconSafe` at module load, so a prohibited word breaks the
 *    screen in development instead of reaching a parent.
 * 2. **It is reviewable.** The lexicon's v0→v1 note requires the Persian renderings to
 *    be reviewed by a native-speaking child clinician, because a hedge survives
 *    translation while its implicature does not. That review is possible against one
 *    file and impractical against thirty JSX literals.
 *
 * A build check (`scripts/check-copy.ts`) enforces that no other source file under
 * `src/` contains Persian text, so the guard cannot be bypassed by writing a string
 * straight into a component.
 */

/**
 * A leaf is a sentence or, where the copy runs long, a list of paragraphs.
 *
 * Arrays rather than newlines inside one string: a paragraph break is structure, and encoding it
 * in whitespace means every renderer has to agree on how to split it back out.
 */
type CopyLeaf = string | readonly string[];
type CopyTree = { readonly [key: string]: CopyLeaf | CopyTree };

/**
 * Walks the tree and refuses prohibited vocabulary, naming the path so the failure says
 * which string is at fault.
 *
 * This throws at import time, which takes the whole screen down rather than degrading.
 * That is the specified behaviour and not an oversight: the lexicon requires an
 * unlicensed sentence to be *unrenderable*, and a surface that cannot render honestly
 * must not render. It can only fire after a source edit that the build check already
 * rejects, so the blast radius is a developer's screen, not a user's.
 */
function guarded<T extends CopyTree>(tree: T, path = "copy"): T {
  for (const [key, value] of Object.entries(tree)) {
    const where = `${path}.${key}`;
    if (typeof value === "string") assertLexiconSafe(value, where);
    else if (Array.isArray(value)) {
      value.forEach((paragraph, i) => assertLexiconSafe(paragraph as string, `${where}[${i}]`));
    } else guarded(value as CopyTree, where);
  }
  return tree;
}

/**
 * Substitutes `{name}` placeholders.
 *
 * Parameterised copy stays a plain string rather than becoming a template function so
 * that the guard above can still see it — a function body is opaque to the walker, and
 * "the interpolated one" is exactly the string a prohibited word would hide in.
 */
export function fill(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => {
    const value = vars[key];
    if (value === undefined) {
      // Louder than rendering the literal "{count}" to a parent, and it can only happen
      // through a caller mistake.
      throw new Error(`missing copy variable "${key}" in: ${template}`);
    }
    return String(value);
  });
}

/**
 * A task's child-facing name, falling back to its id.
 *
 * The fallback is visible rather than blank on purpose: a task shipped in a bundle
 * without a label is a content bug, and showing the id makes it obvious in one glance
 * instead of rendering an unnamed button.
 */
export function taskLabel(taskId: string): string {
  const labels: Record<string, string> = COPY.child.tasks;
  return labels[taskId] ?? taskId;
}

/**
 * A task's name shortened to fit a column head, falling back to the full one.
 *
 * **A second table rather than a truncation of the first**, because Persian game names do not
 * shorten mechanically: "طوطی حرف‌شناس" cut to its first word is "طوطی", which names the parrot and
 * not the alphabet. Every entry here is a word a teacher can recognise the game by on its own, which
 * is the only thing a column head has room to be.
 *
 * Falling back to the full name is deliberate too: a wide column is ugly and a nameless one is a
 * bug, so a game missing from this table looks wrong rather than looking empty.
 */
export function taskShortLabel(taskId: string): string {
  const labels: Record<string, string> = COPY.child.tasksShort;
  return labels[taskId] ?? taskLabel(taskId);
}

/**
 * What a game is for, in a teacher's words. Empty rather than a fallback.
 *
 * Blank rather than the id here, unlike the name lookups: this line sits under a name that is
 * already correct, so a missing entry leaves a tidy row with one line instead of a row with a slug
 * in it. The test that requires an entry for every game is what catches the omission.
 */
export function taskPurpose(taskId: string): string {
  const labels: Record<string, string> = COPY.child.tasksPurpose;
  return labels[taskId] ?? "";
}

/** The one-line hint on a game card. Empty rather than a fallback — better blank than an id. */
/**
 * An objective's name, or its id when the table has no entry.
 *
 * The id rather than an empty string, for the same reason `taskLabel` does it: a missing name shows
 * up as an ugly slug on screen and gets fixed, where a blank row in the class grid would look like a
 * child with no data.
 */
export function objectiveLabel(objectiveId: string): string {
  const labels: Record<string, string> = COPY.objectives;
  return labels[objectiveId] ?? objectiveId;
}

export function strandLabel(strand: string): string {
  const labels: Record<string, string> = COPY.strands;
  return labels[strand] ?? strand;
}

export function taskHint(taskId: string): string {
  const hints: Record<string, string> = COPY.child.hints;
  return hints[taskId] ?? "";
}

export const COPY = guarded({
  /**
   * The public site: landing and about.
   *
   * **This is where the Claim Paradox is decided, and this copy does not decide it.**
   *
   * What a parent shopping for "استعدادیابی" wants to be told is that we will identify their
   * child's talent. The measurement ceiling is ~10–25% of criterion variance `[FACT: S1, S2]` and
   * no norms exist for Persian-speaking children, so that sentence has nothing behind it — and the
   * word itself is refused by the lexicon guard, which is why it appears nowhere below.
   *
   * The honest pitch here is a DIFFERENT one, and whether it converts is H1, the rank-one unknown
   * on the register: the product shows a parent what their child gravitates to, sticks with, and
   * works through, and the parent draws the conclusion. We describe; they infer. That is the line,
   * and it is the only version of "discovery" the evidence supports.
   *
   * Written so the H1 test is possible rather than foreclosed: the hero, the promise and the
   * closing line are separate keys, so a second variant is a copy change and not a rewrite.
   */
  site: {
    /** The brand name. In the table like everything else — the guard has no exemptions. */
    brand: "کیدورا",
    /** Alt text for the Enamad trust seal in the footer — everything else about that badge is theirs. */
    enamadAlt: "نماد اعتماد الکترونیکی",
    nav: {
      // Short on purpose: this sits beside the wordmark in the header, where "…ی کیدورا" would only
      // repeat the brand name already on screen right next to it.
      about: "درباره",
      enter: "ورود",
      /**
       * Registering is a way IN, not something found inside the way in.
       *
       * It used to be a link at the bottom of the sign-in card, which put a five-field form for
       * teachers underneath a phone box that is mostly used by the parent of a five-year-old. The two
       * are different errands with different audiences, so registering gets its own door here.
       */
      apply: "ثبت‌نام معلم",
      play: "بازی‌ها",
      classroom: "کلاس من",
      back: "بازگشت",
    },

    /**
     * The hero.
     *
     * **Addressed to a teacher, and the pivot is most visible here.** The previous version spoke to a
     * parent about seeing how their child thinks — a promise about the child's mind. This one promises
     * a loop the teacher runs: set a goal, the child plays, you see who practised what, the system
     * suggests the next thing. That is a promise about a tool rather than about a person, and it is
     * the one this product can keep.
     */
    heroEyebrow: "برای مربیان پیش‌دبستانی",
    heroTitle: "هدف را شما تعیین کنید، بقیه‌اش با کیدورا",
    heroLead:
      "شاگردهایتان را ثبت می‌کنید، هدف آموزشی می‌دهید، و آن‌ها با بازی تمرین می‌کنند. شما می‌بینید هر کسی چه چیزی را یاد گرفته و بعدی چه باید باشد.",
    heroCta: "شروع کنید",
    heroSecondary: "بیشتر بدانید",

    /**
     * The teacher's other door, on the page a teacher lands on.
     *
     * "شروع کنید" goes to sign-in, which is right for the teacher who already has an account and a
     * dead end for the one who does not — and the sign-in screen no longer offers a way to register.
     * So the offer is made here instead, where somebody deciding whether this is worth an afternoon
     * is already reading.
     */
    teacherPrompt: "هنوز حساب ندارید؟",
    teacherCta: "ثبت‌نام معلم",

    /**
     * The loop, in the four steps it actually has.
     *
     * Replaces the old three-step "how it works", which described a parent signing up and waiting. The
     * loop is the product now, so it goes on the page as a loop — a teacher deciding whether this is
     * worth an afternoon needs to see where their own work fits.
     */
    loopTitle: "چرخه‌ی کار",
    loop: {
      oneTitle: "هدف می‌دهید",
      oneBody: "از میان اهدافی مثل «شمارش تا ۵» یا «الگوی تکرارشونده»، به یک شاگرد یا کل کلاس.",
      twoTitle: "کودک بازی می‌کند",
      twoBody: "سیستم همان بازی را از همان مرحله‌ای که باید باز می‌کند — نه از اول.",
      threeTitle: "شما می‌بینید",
      threeBody: "چه کسی چه چیزی را تمرین کرده، چه چیزی را یاد گرفته، و کجا گیر کرده است.",
      fourTitle: "پیشنهاد بعدی می‌آید",
      fourBody: "همراه با دلیلش، تا بتوانید قبولش کنید یا کنارش بگذارید.",
    },

    /**
     * The games, shown rather than described.
     *
     * A teacher can read a whole page about a tool and still not know what the child will see. The
     * count is computed from the bundle, never typed here, so it cannot drift from what ships.
     */
    gamesTitle: "بازی‌هایی که تمرین در آن‌ها اتفاق می‌افتد",
    gamesLead: "چند نمونه از بازی‌ها؛ هر کدام چند دقیقه طول می‌کشد و هیچ‌کدام برنده و بازنده ندارد.",
    gamesCount: "{count} بازی در کیدورا",

    /**
     * What is absent, as a promise.
     *
     * **"No comparison" is gone from this list, and that is the honest change.** It was a promise the
     * old product kept and this one deliberately breaks: a teacher comparing children in their own
     * class is the feature that was asked for (ADR-011). Leaving the promise up while shipping the
     * comparison would have been the one dishonesty this file cannot afford. What replaced it is the
     * boundary that still holds — the comparison never leaves the room.
     */
    promisesTitle: "چند چیز که اینجا پیدا نمی‌کنید",
    promises: {
      adsTitle: "بدون تبلیغات",
      adsBody: "چیزی به کودک نشان داده یا فروخته نمی‌شود.",
      scopeTitle: "بدون مقایسه با بیرون",
      scopeBody:
        "مقایسه فقط میان شاگردان همین کلاس است. هیچ سنجشی با کودکان بیرون از کلاس شما در کار نیست.",
      scoreTitle: "بدون نمره برای کودک",
      scoreBody: "کودک هیچ نمره‌ای نمی‌بیند و جای هیچ‌کس را در کلاس نمی‌داند.",
      labelTitle: "بدون برچسب",
      labelBody: "چیزی درباره‌ی شخصیت یا آینده‌ی کودک گفته نمی‌شود.",
    },

    /** Three things the teacher's surface actually shows. Each is something the record supports. */
    valueTitle: "چه چیزی می‌بینید",
    values: {
      learntTitle: "چه کسی چه چیزی را یاد گرفته",
      learntBody:
        "برای هر هدف، هر شاگرد در یکی از سه حال است: یاد گرفته، در حال تمرین، یا شروع نکرده. نه درصدی، نه نمره‌ای.",
      compareTitle: "کجای کلاس ایستاده‌اند",
      compareBody:
        "در هر بازی می‌بینید هر شاگرد به کدام مرحله رسیده و میانه‌ی کلاس کجاست — تا بدانید کدام بچه به کمک بیشتری نیاز دارد.",
      nextTitle: "بعدی چه باشد",
      nextBody:
        "برای هر شاگرد یک فعالیت پیشنهاد می‌شود، همراه با دلیلش. اگر با آن موافق نبودید، خودتان هدف بدهید.",
    },

    /**
     * The honesty section, on the landing page rather than in the small print.
     *
     * Shorter than the version it replaces, because less of it is needed: a product that reports work
     * done in a classroom has less to walk back than one that described a child's mind. What stays is
     * the part that is still true and still uncomfortable.
     */
    honestTitle: "و چه چیزی به شما نمی‌گوید",
    honestBody: [
      "کیدورا آزمون نیست و درباره‌ی توانایی‌های کلی یک کودک حرفی نمی‌زند. چیزی که نشان می‌دهد کاری است که کودک در همین بازی‌ها انجام داده.",
      "مقایسه‌ی کلاس هم همین‌قدر محدود است: میان بچه‌های همین کلاس و بر پایه‌ی همین بازی‌ها. شاگردی که تازه شروع کرده، عقب حساب نمی‌شود.",
    ],

    closingTitle: "آماده‌اید شروع کنید؟",
    closingBody: "با شماره‌ی موبایل خودتان وارد شوید. ساختن کلاس یک دقیقه طول می‌کشد.",

    /**
     * About — longer, calmer, and the place the reasoning goes.
     *
     * **Rewritten because it was four paragraphs and two boxes**, which is a page that has been
     * started rather than written: a teacher deciding whether to set up a class got the pitch again in
     * slower words and no answer to any of the questions they would actually ask. What follows is
     * ordered the way somebody reads it — what this is, how a week with it goes, what the games are,
     * how "learnt" is decided, what is compared, what is stored, and what it refuses to claim.
     *
     * The last of those is not a disclaimer at the end. It is the argument: the measurement ceiling
     * for tasks like these is a fraction of what a person is, no Persian norms exist to place a child
     * against, and any product promising otherwise is promising something nobody can deliver. Saying
     * so is what makes the rest of the page believable.
     */
    aboutTitle: "درباره‌ی کیدورا",
    aboutBody: [
      "کیدورا ابزاری برای کلاس پیش‌دبستانی است، نه سرگرمی‌ای که کنارش گزارش هم بدهد. مربی تصمیم می‌گیرد این هفته روی چه چیزی کار شود؛ کودک همان را با بازی تمرین می‌کند؛ و مربی می‌بیند تمرین تا کجا پیش رفته و قدم بعدی چه باید باشد.",
      "کل کار در یک چرخه می‌گذرد: شما هدفی مثل «شمارش تا ۱۰» یا «الگوی رنگی» را به کلاس می‌دهید، بازی مربوط به آن برای شاگردها باز می‌شود، و هر کودک از همان مرحله‌ای شروع می‌کند که خودش تا آن‌جا رسیده — نه از اول. آن‌ها بازی می‌کنند، سیستم ثبت می‌کند، و شما در صفحه‌ی کلاس می‌بینید چه کسی چه چیزی را تمرین کرده و چه کسی هنوز شروع نکرده است.",
    ],

    /** The sections. Each one answers a question a teacher would actually ask before setting up a class. */
    aboutGamesTitle: "بازی‌ها چه شکلی‌اند",
    aboutGamesBody: [
      "{count} بازی، در چند دسته‌ی جدا از هم: شمردن و حساب، حرف‌ها، الگو و دنیای رنگی، جدول شکل‌ها، هزارتو و مهندس ربات، حافظه و توجه.",
      "بیشترشان عمداً بدون متن‌اند تا توانایی خواندن در نتیجه‌شان اثر نگذارد؛ کودکی که هنوز نمی‌خواند هم باید بتواند کل بازی را فقط از روی تصویر بفهمد. دو استثنا داریم و هر دو عمدی است: در «شمردن و حساب» و «جدول شکل‌ها» خودِ رقم چیزی است که آموزش داده می‌شود، و در «حرف‌ها» خودِ شکل حرف.",
      "هر بازی چند مرحله دارد، و مرحله‌ها با هم فرق می‌کنند نه اینکه فقط سخت‌تر شوند: شمارش از عدد چهار شروع می‌شود و تا بیست می‌رود؛ الگو از «قرمز، آبی، قرمز» شروع می‌شود و به الگوهای پیچیده‌تر می‌رسد.",
    ],

    aboutTeachingTitle: "چرا اشتباه بازی را تمام نمی‌کند",
    aboutTeachingBody: [
      "اگر کودک چند بار پشت‌سرهم اشتباه کند، بازی یک مرحله ساده‌تر می‌شود و ادامه پیدا می‌کند؛ جواب درست را هم به او نشان می‌دهد.",
      "این تصمیم، مهم‌ترین تفاوت کیدورا با یک آزمون است. آزمون باید همان‌جا که کودک گیر می‌کند بایستد، چون ادامه دادنش به‌جای سنجیدن ناتوانی، فقط ناکامی کودک را ثبت می‌کند. اما یادگیری دقیقاً همان‌جا اتفاق می‌افتد — همان نوبتِ بعد از اشتباه — و بازی برای دقیقاً همین لحظه ساخته شده.",
      "یک نشست وقتی تمام می‌شود که نوبت‌هایش تمام شده باشد، نه وقتی کودک چند بار اشتباه کرده باشد. هیچ بازی‌ای به کودک نمی‌گوید «کافی است».",
    ],

    aboutMasteryTitle: "«یاد گرفت» یعنی چه",
    aboutMasteryBody: [
      "هر هدف به بازه‌ی مشخصی از مرحله‌های یک بازی وصل است. وقتی کودک در بالاترین مرحله‌ی آن بازه چند نوبت پشت‌سرهم درست جواب بدهد، آن هدف «یاد گرفته» می‌شود.",
      "همین قاعده، کودک را در خودِ بازی هم یک مرحله بالا می‌برد؛ یعنی قاعده‌ای که وسط بازی او را جلو می‌برد و قاعده‌ای که هدف را تمام‌شده اعلام می‌کند یکی هستند، و این دو هیچ‌وقت نمی‌توانند با هم اختلاف پیدا کنند.",
      "هیچ‌چیز از قبل ذخیره نمی‌شود: این وضعیت هر بار از روی خودِ نوبت‌های ثبت‌شده حساب می‌شود. برای همین هیچ‌وقت کهنه نمی‌ماند و هیچ‌وقت با آن‌چه واقعاً اتفاق افتاده اختلاف پیدا نمی‌کند.",
    ],

    aboutCompareTitle: "مقایسه، و مرزش",
    aboutCompareBody: [
      "بله، می‌توانید شاگردها را با هم مقایسه کنید — ولی فقط میان بچه‌های یک کلاس، و فقط بر پایه‌ی همین بازی‌ها.",
      "شاگردی که در یک بازی کمتر از پنج نوبت داشته، در میانه‌ی کلاس حساب نمی‌شود و در فهرست «کسانی که به کمک نیاز دارند» هم نمی‌آید؛ چون بازی کردن فقط یک بار به معنای عقب بودن نیست. هر بازی همه را از صفر شروع می‌کند، پس بدون این قاعده نیمی از کلاس «ضعیف» به نظر می‌رسید.",
      "بیرون از کلاس هیچ مقایسه‌ای وجود ندارد: برای کودکان فارسی‌زبان در این نوع تکلیف‌ها هیچ هنجار سراسری‌ای وجود ندارد، و ما هم قصد ساختن چنین چیزی را نداریم.",
    ],

    aboutControlTitle: "کنترل با شماست",
    aboutControlBody: [
      "هیچ بازی‌ای تا وقتی خودتان بازش نکنید، برای کلاس فعال نمی‌شود. بازی‌های بسته در صفحه‌ی کودک دیده می‌شوند، ولی خاکستری و قفل‌اند — کودک می‌بیند چه چیزی در راه است، بی‌آنکه بتواند سراغش برود.",
      "دادن یک هدف به کلاس، بازی مربوط به همان هدف را هم باز می‌کند؛ چون هدفی که کودک نتواند تمرینش کند، اصلاً هدف نیست.",
    ],

    /**
     * Who the games were designed under.
     *
     * A named person rather than "designed by experts": a claim about expertise that names nobody is a
     * claim nobody can check, and this page's whole argument is that everything on it can be checked.
     */
    aboutSupervisionTitle: "زیر نظر چه کسی",
    aboutSupervisionBody:
      "این بازی‌ها زیر نظر مربی گران‌قدر، **خانم کوکبی**، طراحی شده‌اند. ترتیب هدف‌ها، نقطه‌ی شروع و سختیِ هر بازی، و اینکه یک کودک پیش‌دبستانی در هر مرحله از پسِ چه کاری برمی‌آید — همه با نظر ایشان تعیین شده‌اند.",
    aboutAgeTitle: "برای چه کسی",
    aboutAgeBody:
      "مربیان پیش‌دبستانی و کودکان کلاسشان. مرحله‌های بالای بازی‌ها برای بچه‌های بزرگ‌تر هم جا دارد، ولی نقطه‌ی شروع، پیش‌دبستانی است.",
    aboutPrivacyTitle: "درباره‌ی اطلاعات",
    aboutPrivacyBody:
      "از مربی نام، نام خانوادگی، کد ملی و نام پیش‌دبستانی می‌گیریم؛ از شاگرد فقط نام و شماره‌ای که مربی وارد می‌کند. پیشرفت هر شاگرد فقط برای مربی خودش قابل دیدن است، و هر بار پرونده‌ی کودکی باز شود، ثبت می‌شود چه کسی آن را دیده است. اگر شاگردی از کلاس حذف شود، نام و شماره‌اش پاک می‌شود و کارهایی که کرده، بی‌نام، در تاریخچه‌ی کلاس باقی می‌ماند.",

    /**
     * What it does not claim.
     *
     * Deliberately the longest section and deliberately not last-minute small print: it is the reason
     * to trust the rest.
     *
     * **Written without naming the words it refuses, because the guard refuses them here too.** The
     * first draft said the product does not place a child in a صدک — a denial, and the lexicon guard
     * rejected it anyway. That is the guard being right rather than crude: a sentence containing the
     * word is a sentence that can be quoted without its negation, and a screenshot of half of it is
     * exactly the claim the ban exists to prevent.
     */
    aboutLimitsTitle: "چه چیزی نمی‌گوییم",
    aboutLimitsBody: [
      "کیدورا نمره‌ای از صد به کودک نمی‌دهد، جایگاهی میان بچه‌های دیگر برایش تعیین نمی‌کند، و درباره‌ی توانایی‌های ذاتی‌اش هیچ ادعایی نمی‌کند.",
      "آنچه این بازی‌ها می‌سنجند، تنها بخش کوچکی از آن چیزی است که یک کودک هست؛ و برای کودکان فارسی‌زبان هیچ هنجاری وجود ندارد که بشود کسی را با آن سنجید. هر محصولی که خلاف این را وعده بدهد، وعده‌ای داده که هیچ‌کس نمی‌تواند به آن عمل کند.",
      "کاری که کیدورا می‌کند این است: توصیف می‌کند کودک چه چیزی را تمرین کرده، تا کجا پیش رفته، و کجا گیر کرده است. نتیجه‌گیری با شماست — شما او را می‌شناسید، ما نه.",
    ],

    aboutStartTitle: "چطور شروع می‌شود",
    aboutStartBody: [
      "درخواست ثبت‌نام می‌دهید؛ بعد از بررسی، کلاستان باز می‌شود و با همان شماره وارد پنل می‌شوید.",
      "بررسی را یک نفر انجام می‌دهد، پس ممکن است کمی طول بکشد. این تأخیر عمدی است: پشت هر تأییدی که می‌دهیم، پرونده‌ی یادگیریِ یک کلاس پر از کودکان پنج‌ساله قرار دارد.",
    ],
  },

  /** Child-facing. Describes the activity, never the child. */
  child: {
    preparing: "در حال آماده‌سازی…",
    /** Offered when a screen fails to draw at all. Reload, not retry — see `error-boundary.tsx`. */
    reload: "دوباره باز کن",
    failed: "مشکلی پیش آمد: {message}",
        backToGames: "بازگشت به بازی‌ها",
    /** The child's shelf when the teacher has opened nothing yet. */
    nothingOpen: "هنوز بازی‌ای برایت باز نشده.",
    nothingOpenHint: "معلمت به‌زودی بازی‌ها را باز می‌کند.",
    signOut: "خروج",
    soundOn: "صدا روشن",
    soundOff: "صدا خاموش",

    /** The card above the shelf: what the teacher asked for, or what comes next. */
    today: {
      assigned: "کارِ امروز",
      unfinished: "نیمه‌کاره مانده",
      progression: "مرحله‌ی بعد",
      starting: "بیا این را شروع کنیم",
      caughtUp: "همه‌چیز را یاد گرفتی! هر بازی‌ای دوست داری انتخاب کن.",
      start: "شروع",
    },
    /** The rest of the shelf, below the card. Free choice never goes away. */
    freeChoice: "یا هر بازی دیگری که دوست داری",

    // Task names name the GAME, not a capacity. "بازی حافظه" would tell a child what we think
    // we are measuring in them, which is a label (v2 §19 — talent erasure). Each name is the
    // creature on its card, so a child who cannot read yet still knows which is which.
    tasks: {
      "wm-span-forward-bare": "کرم‌های شب‌تاب",
      "wm-span-forward-story": "شب‌تاب‌های جنگل",
      "wm-span-backward-bare": "جغد وارونه",
      "vs-pattern-match-bare": "ماهی‌های همسان",
      "vs-odd-one-out-bare": "گربه‌ی تیزبین",
      "vs-build-copy-bare": "ستاره‌ساز",
      "vs-maze-bare": "هزارتوی جادویی",
      "vs-robot-bare": "مهندس ربات",
      "vs-sequence-bare": "دنیای گمشده",
      "vs-sequence-color-bare": "دنیای رنگی",
      "lit-letters-bare": "طوطی حرف‌شناس",
      "lit-find-letter-bare": "ذره‌بین",
      "vs-difference-bare": "چشم تیزبین",
      "ef-rule-switch-bare": "آفتاب‌پرست",
      "wm-pairs-bare": "جفت‌های پنهان",
      "vs-ordering-bare": "کوچک به بزرگ",
      "math-arith-bare": "شمردن و حساب",
      "logic-sudoku-bare": "جدول شکل‌ها",
      "vs-shadow-bare": "خفاش سایه‌شناس",
      "vs-mirror-bare": "پروانه‌ی قرینه",
      "vs-parts-bare": "سگ آبی سازنده",
      "vs-search-bare": "مورچه‌ی جست‌وجوگر",
      "vs-code-key-bare": "هشت‌پای رنگ‌ساز",
      "vs-tiling-bare": "حلزون نقش‌انداز",
      "ef-key-map-bare": "خرگوش راننده",
    },
    /**
     * The same games, in one word each, for a table column.
     *
     * Teacher-facing rather than child-facing, and the only place a game is named by what it TEACHES
     * instead of by its creature: a column head has room for one word, and "شمردن" tells a teacher
     * which column they are reading where "زنبور" does not. The child's shelf still uses the names
     * above, which name the creature and never the capacity (v2 §19).
     */
    tasksShort: {
      "wm-span-forward-bare": "شب‌تاب",
      "wm-span-forward-story": "جنگل",
      "wm-span-backward-bare": "وارونه",
      "vs-pattern-match-bare": "همسان",
      "vs-odd-one-out-bare": "متفاوت",
      "vs-build-copy-bare": "ساختن",
      "vs-maze-bare": "هزارتو",
      "vs-robot-bare": "ربات",
      "vs-sequence-bare": "الگو",
      "vs-sequence-color-bare": "رنگ",
      "lit-letters-bare": "حروف",
      "lit-find-letter-bare": "ذره‌بین",
      "vs-difference-bare": "تفاوت",
      "ef-rule-switch-bare": "قاعده",
      "wm-pairs-bare": "جفت",
      "vs-ordering-bare": "اندازه",
      "math-arith-bare": "شمردن",
      "logic-sudoku-bare": "جدول",
      "vs-shadow-bare": "سایه",
      "vs-mirror-bare": "قرینه",
      "vs-parts-bare": "قطعه",
      "vs-search-bare": "جست‌وجو",
      "vs-code-key-bare": "کلید",
      "vs-tiling-bare": "نقش",
      "ef-key-map-bare": "راننده",
    },
    /**
     * What each game is FOR, in a teacher's words.
     *
     * A second table beside `hints`, and the reason is the reason `tasksShort` is a second table
     * beside `tasks`: two readers, two sentences. `hints` is written at a five-year-old — "همان شکل
     * بالا را پیدا کن" — and it tells a teacher what the child DOES, which is not what they need
     * when deciding whether to open the game for a week on counting.
     *
     * Written as what the child practises, never as a capacity they have or lack. "Remembering the
     * order of a few things" is an activity; anything of the shape "strengthens memory" is a promise
     * about a child that nothing here can keep, and the lexicon guard refuses the words for it.
     */
    tasksPurpose: {
      "math-arith-bare": "شمردن، جمع، تفریق و ضرب — از شمارش تا ۳ تا تقسیم",
      "vs-ordering-bare": "مرتب کردن چند چیز از کوچک به بزرگ",
      "lit-letters-bare": "شناختن حرف‌های الفبا و شکل‌هایی که در کلمه می‌گیرند",
      "lit-find-letter-bare": "پیدا کردن یک حرف در میان کلمه‌ها",
      "vs-sequence-bare": "ادامه دادن الگویی که از شکل‌ها ساخته شده",
      "vs-sequence-color-bare": "ادامه دادن الگوی رنگ‌ها — ساده‌ترین ورود به الگو",
      "vs-tiling-bare": "تکرار کردن یک نقش سه‌ردیفه در طول نوار",
      "vs-pattern-match-bare": "پیدا کردن شکل یکسان میان چند شکل خیلی نزدیک به هم",
      "logic-sudoku-bare": "کنار گذاشتن گزینه‌ها: در هر سطر و ستون، از هر شکل یکی",
      "vs-robot-bare": "چیدن چند دستور پشت سر هم و بعد اجرا کردنشان",
      "vs-code-key-bare": "دنبال کردن یک کلید نوشته‌شده، روی چند مورد پشت سر هم",
      "vs-shadow-bare": "شناختن یک حیوان فقط از روی خط دورش",
      "vs-odd-one-out-bare": "پیدا کردن شکلی که آینه‌ی بقیه است — همان فرق چپ و راست",
      "vs-parts-bare": "دیدن اینکه یک شکل از چه قطعه‌هایی ساخته شده",
      "vs-build-copy-bare": "ساختن دوباره‌ی یک شکل روی صفحه‌ی خالی",
      "vs-mirror-bare": "ساختن قرینه‌ی یک نقش، آن‌طرف خط",
      "vs-maze-bare": "پیدا کردن راه بیرون و نگه داشتن مسیر در ذهن",
      "wm-span-forward-bare": "به یاد آوردن ترتیب چند چیز، به همان ترتیب",
      "wm-span-forward-story": "همان بازی شب‌تاب‌ها، این بار داخل یک قصه",
      "wm-span-backward-bare": "به یاد آوردن ترتیب، ولی از آخر به اول",
      "wm-pairs-bare": "به یاد سپردن اینکه هر کارت کجا بود",
      "vs-difference-bare": "پیدا کردن چند تفاوت کوچک میان دو تصویر",
      "vs-search-bare": "گشتن همه‌ی صفحه تا هیچ‌کدام جا نماند",
      "ef-rule-switch-bare": "فهمیدن اینکه قاعده عوض شده، بی‌آنکه کسی بگوید",
      "ef-key-map-bare": "دنبال کردن کلیدی که وسط بازی جلوی چشم عوض می‌شود",
    },
    /** One short line per game. Minimal text is the rule for this age band. */
    hints: {
      "wm-span-forward-bare": "ببین کدام‌ها روشن شدند و همان‌ها را بزن",
      "wm-span-forward-story": "ببین کدام‌ها روشن شدند و همان‌ها را بزن",
      "wm-span-backward-bare": "روشن‌شده‌ها را از آخر به اول بزن",
      "vs-pattern-match-bare": "همان شکل بالا را پیدا کن",
      "vs-odd-one-out-bare": "سه شکل یکی هستند. کدام‌یک فرق دارد؟",
      "vs-build-copy-bare": "همان شکل را بساز",
      "vs-maze-bare": "راه بیرون رفتن را پیدا کن",
      "vs-robot-bare": "دستورها را بچین، بعد ربات را راه بینداز",
      "vs-sequence-bare": "الگو تکرار می‌شود؛ بعدی کدام است؟",
      "vs-sequence-color-bare": "رنگ‌ها الگو دارند؛ بعدی چه رنگی است؟",
      "lit-letters-bare": "کدام‌یک همان حرف است؟",
      "lit-find-letter-bare": "این حرف در کدام کلمه است؟",
      "vs-difference-bare": "دو تصویر چند جا فرق دارند؟ پیدایشان کن",
      "ef-rule-switch-bare": "کارت را کنار جعبه‌ی هم‌جنسش بگذار. جعبه‌ها گاهی عوض می‌شوند!",
      "wm-pairs-bare": "دو کارت را برگردان و جفتشان را پیدا کن",
      "vs-ordering-bare": "از کوچک‌ترین شروع کن و تا بزرگ‌ترین برو",
      "math-arith-bare": "درست را از میان کارت‌ها پیدا کن",
      "logic-sudoku-bare": "در هر سطر و ستون، از هر شکل فقط یکی",
      "vs-shadow-bare": "این سایه‌ی کدام‌یک است؟",
      "vs-mirror-bare": "بال دیگر پروانه را قرینه‌ی این یکی رنگ کن",
      "vs-parts-bare": "این شکل از کدام قطعه‌ها ساخته شده؟",
      "vs-search-bare": "همه‌ی تیرهایی که مثل بالا هستند را بزن",
      "vs-code-key-bare": "بالا را نگاه کن و هرکدام را رنگ خودش بزن",
      "vs-tiling-bare": "همان نقش را تا آخر نوار ادامه بده",
      "ef-key-map-bare": "بالا را ببین: هر رنگ کدام طرف می‌رود؟ گاهی عوض می‌شود!",
    },
    spanDone: "آفرین! بلندترین دنباله‌ای که درست تکرار کردی: {best}",
    // Counts rather than a proportion: "۶ از ۱۰" is concrete for a six-year-old, and a
    // score out of a hundred is prohibited framing whoever reads it (lexicon §B).
    patternDone: "آفرین! {correct} از {total} الگو را درست پیدا کردی.",
    playAgain: "یک بازی دیگر",

    /**
     * The dialog after a game has demonstrated itself.
     *
     * Text here does not break the no-language rule (v2 §15). That rule is about the STIMULUS —
     * language inside a measured trial means reading ability leaks into the measurement, and there
     * are no local norms to correct for it. This sits between the demonstration and the first
     * scored trial, and it only confirms what the child was already shown, so a child who cannot
     * read it has lost nothing.
     */
    tutorialBody: "این‌طوری بازی می‌شود. حالا نوبت توست.",
    tutorialReplay: "دوباره ببین",
    tutorialStart: "شروع می‌کنم",
  },

  /**
   * The learning objectives, in the words a teacher uses.
   *
   * One entry per objective in the bundle, and this table is the whole reason objectives
   * exist as a separate thing from games (ADR-011): a teacher does not think "level seven of the
   * pattern game", they think "counting to ten". A missing entry falls back to the id, which is ugly
   * on screen and therefore gets noticed — the alternative, an empty string, would silently produce a
   * blank row in the class grid.
   */
  objectives: {
    "count-to-3": "شمارش تا ۳",
    "count-to-5": "شمارش تا ۵",
    /**
     * The letters strand.
     *
     * Named for the SHAPE of a letter, never for its sound: nothing here plays audio, and an objective
     * called «صداها» would be claiming to teach something the product cannot say out loud.
     */
    "letter-tell-apart": "تشخیص حرف‌های متفاوت",
    "letter-close-shapes": "حرف‌های شبیه هم",
    "letter-dots": "نقطه‌ها را ببین",
    "letter-joined-forms": "شکل‌های حرف در کلمه",
    "letter-name-the-form": "این شکل، کدام حرف است؟",
    /**
     * Finding a letter inside a word — the same skill as the letters game, one step further out.
     *
     * Named for looking rather than for reading: the child is not being asked what the word says.
     */
    "find-letter-in-word": "پیدا کردن حرف در کلمه",
    "find-letter-among-lookalikes": "حرف را میان شبیه‌هایش پیدا کن",
    "find-letter-inside-word": "حرف در وسط کلمه",
    "find-letter-in-long-word": "حرف در کلمه‌ی بلند",
    "count-to-10": "شمارش تا ۱۰",
    "count-to-20": "شمارش تا ۲۰",
    "count-at-a-glance": "تشخیص تعداد در یک نگاه",
    "add-to-5-objects": "جمع تا ۵ با اشیاء",
    "add-to-5-digits": "جمع تا ۵ با عدد",
    "add-to-10": "جمع تا ۱۰",
    "subtract-to-5": "تفریق تا ۵",
    "subtract-to-10": "تفریق تا ۱۰",
    "add-subtract-mixed": "جمع و تفریق با هم",
    "add-to-20": "جمع تا ۲۰",
    "multiply-groups": "ضرب با گروه‌های مساوی",
    multiply: "ضرب",
    divide: "تقسیم",
    "order-by-size": "مرتب کردن از کوچک به بزرگ",
    "order-by-size-close": "مرتب کردن اندازه‌های نزدیک",

    "pattern-color-repeat": "الگوی رنگی ساده",
    "pattern-color-three": "الگوی رنگی سه‌تایی",
    "pattern-color-structured": "الگوی رنگی ساختاردار",
    "pattern-color-hard": "الگوی رنگی دشوار",
    "pattern-repeat": "الگوی تکرارشونده",
    "pattern-two-things": "الگو با شکل و رنگ",
    "pattern-structured": "الگوی ساختاردار",
    "pattern-growing": "الگوی رشدی",
    "carry-a-motif": "ادامه دادن نقش",
    "carry-a-wider-motif": "نقش پهن‌تر",
    "carry-a-full-motif": "نقش پر و رنگی",
    "pattern-match": "پیدا کردن شکل همسان",
    "pattern-match-close": "شکل‌های خیلی شبیه",

    "sudoku-shapes": "جدول شکل‌ها",
    "sudoku-shapes-deeper": "جدول شکل‌ها با خانه‌های بیشتر",
    "sudoku-digits": "جدول عددها",
    "follow-a-key": "رنگ کردن با کلید",
    "follow-a-longer-key": "کلید بلندتر",
    "follow-a-counted-key": "کلید با شمردن",
    "robot-basics": "راه بردن ربات",
    "robot-obstacles": "ربات و موانع",
    "robot-long-programs": "برنامه‌های بلند ربات",

    "match-a-shadow": "پیدا کردن سایه",
    "match-a-shadow-alike": "سایه‌های شبیه هم",
    "match-a-shadow-crowd": "سایه در میان شلوغی",
    "mirror-shapes": "شکل و قرینه‌اش",
    "fold-a-shape": "قرینه‌ی جای نقطه‌ها",
    "fold-a-shape-more": "قرینه با دو رنگ",
    "fold-a-shape-complex": "قرینه با سه رنگ",
    "mirror-shapes-close": "قرینه‌های نزدیک",
    "shape-from-pieces": "قطعه‌های یک شکل",
    "shape-from-more-pieces": "شکل‌های چندقطعه‌ای",
    "shape-from-pieces-colours": "قطعه‌ها با رنگ درست",
    "copy-shape": "ساختن شکل",
    "copy-shape-complex": "ساختن شکل‌های پیچیده‌تر",
    "find-a-way-out": "پیدا کردن راه خروج",
    "find-a-way-out-larger": "هزارتوهای بزرگ‌تر",

    "recall-order": "به یاد آوردن ترتیب",
    "recall-order-longer": "ترتیب‌های بلندتر",
    "recall-reversed": "ترتیب برعکس",
    "recall-reversed-longer": "ترتیب برعکسِ بلندتر",
    "remember-where": "یادآوری جای کارت‌ها",
    "remember-where-more": "کارت‌های بیشتر",

    "spot-a-change": "پیدا کردن تفاوت",
    "spot-a-small-change": "تفاوت‌های کوچک",
    "find-them-all": "پیدا کردن همه‌ی هم‌جهت‌ها",
    "find-them-all-larger": "جست‌وجو در صفحه‌ی بزرگ‌تر",
    "find-them-all-close": "جهت‌های نزدیک به هم",
    "notice-the-rule-changed": "عوض شدن قاعده",
    "follow-a-key-that-changes": "کلیدی که عوض می‌شود",
    "follow-a-key-that-changes-often": "کلیدی که زود عوض می‌شود",
    "follow-a-key-that-barely-settles": "کلید بی‌قرار",
    "notice-the-rule-changed-often": "عوض شدن پی‌درپی قاعده",

  },

  /** The seven areas of the week an objective can belong to. */
  strands: {
    number: "عدد و شمارش",
    letters: "حرف‌ها",
    pattern: "الگو",
    logic: "استدلال",
    space: "فضا و جهت",
    memory: "حافظه",
    focus: "توجه",
  },

  /**
   * Signing in. One screen for both roles, because there is one flow for both.
   *
   * Nothing here says "teacher" or "child": the server decides which you are from whether the number
   * is already on a class roster (R-2), and a screen that asked would be a screen that could be
   * answered wrongly.
   */
  enter: {
    title: "ورود",
    intro: "شماره‌ی موبایل خود را وارد کنید تا یک کد برایتان بفرستیم.",
    phoneLabel: "شماره موبایل",
    phonePlaceholder: "۰۹۱۲۳۴۵۶۷۸۹",
    sendCode: "فرستادن کد",
    working: "کمی صبر…",
    codeTitle: "کد را وارد کنید",
    codeSent: "کدی به {phone} فرستادیم.",
    codeLabel: "کد شش‌رقمی",
    verify: "ورود",
    changeNumber: "شماره را عوض می‌کنم",
    resend: "فرستادن دوباره‌ی کد",
    resendIn: "فرستادن دوباره تا {seconds} ثانیه‌ی دیگر",
    badPhone: "این شماره‌ی موبایل درست به نظر نمی‌رسد.",
    badCode: "این کد درست نیست. دوباره امتحان کنید.",
    cooldown: "به‌تازگی کدی فرستاده‌ایم. کمی صبر کنید.",
    quota: "امروز چند بار کد خواسته‌اید. فردا دوباره امتحان کنید.",
    smsFailed: "فرستادن پیامک ممکن نشد. کمی بعد دوباره امتحان کنید.",
    deleted: "این شماره به حسابی تعلق دارد که حذف شده است.",
    failed: "مشکلی پیش آمد. دوباره امتحان کنید.",
    /**
     * Said plainly, because the alternative is an adult wondering why a five-year-old was asked for
     * a phone number.
     */
    childNote:
      "اگر معلم شماره‌ی شما را برای فرزندتان ثبت کرده، همین کد او را وارد بازی می‌کند. یک‌بار وارد شوید و گوشی را به او بدهید.",

    /**
     * The three ways a correct code leads nowhere (ADR-012).
     *
     * Each says who can do something about it, because that is the only useful part. "ثبت‌نام شما کامل
     * نیست" on its own leaves a parent pressing the same button; it has to say that the teacher is the
     * one who adds a child's number, and it has to leave the teacher a way to apply.
     */
    notRegistered: {
      title: "این شماره ثبت نشده است",
      childBody:
        "اگر فرزندتان شاگرد یک پیش‌دبستانی است، معلمش باید شماره‌ی شما را در کلاس ثبت کند. تا آن موقع ورود ممکن نیست — از معلم بخواهید شماره را اضافه کند.",
      teacherBody: "اگر معلم هستید و می‌خواهید کلاس داشته باشید، از این‌جا درخواست بدهید.",
      apply: "درخواست ثبت‌نام معلم",
      retry: "شماره‌ی دیگری امتحان می‌کنم",
    },
    pending: {
      title: "درخواست شما در حال بررسی است",
      body: "درخواست ثبت‌نام با این شماره ثبت شده و منتظر بررسی است. وقتی تأیید شد، کلاس شما باز می‌شود و با همین شماره می‌توانید وارد پنل شوید.",
      /** No promised date. A queue one person reads cannot be promised in hours. */
      wait: "بررسی درخواست‌ها دستی انجام می‌شود، پس ممکن است کمی طول بکشد.",
    },
    rejected: {
      title: "این درخواست تأیید نشد",
      body: "درخواست ثبت‌نام با این شماره بررسی و تأیید نشد.",
      reason: "دلیلی که نوشته شده: {note}",
      /** Reapplying is allowed — the pending indexes are partial precisely so it can be. */
      again: "درخواست تازه می‌دهم",
    },
    /**
     * A fourth way a correct code leads nowhere, beside the three above: the account is real
     * and the code is right, but the role is one this app has no screen for. Today that is only
     * an operator's account (ADR-013) signing in here instead of on the panel — this app has a
     * teacher surface and a child surface and nothing else, and an admin is neither.
     */
    wrongApp: {
      body: "این شماره به یک حساب مدیریتی (ادمین) تعلق دارد، نه معلم یا شاگرد. برای ورود، به نشانی da.kiddora.ir بروید.",
      signOut: "خروج از این حساب",
    },
  },

  /**
   * A teacher asking for a class.
   *
   * Its own screen rather than a longer sign-in, because it is a different act with a different
   * outcome: signing in takes seconds and either works or does not, while this is a form somebody
   * reads and answers later. Mixing them made the way in look like a registration wall to the parent
   * of a five-year-old, who has nothing to register.
   */
  apply: {
    /** The page says who it is for before it says what it is: nobody else should be filling this in. */
    eyebrow: "ویژه‌ی مربیان پیش‌دبستانی",
    title: "ثبت‌نام معلم",
    lead: "یک‌بار پر می‌کنید. بعد از بررسی، کلاس شما باز می‌شود و می‌توانید شاگردهایتان را ثبت کنید.",

    /**
     * What happens after the button is pressed, said before it is pressed.
     *
     * This is the part the old card had no room for, and its absence was the whole problem: somebody
     * handing over a national id has no idea whether they are creating an account or joining a queue.
     * Three steps, ending where they end — with a person reading it, and no date promised.
     */
    stepsTitle: "بعد از فرستادن درخواست",
    steps: {
      one: "کدی که پیامک می‌شود را وارد می‌کنید و درخواست ثبت می‌شود.",
      two: "درخواست را یک نفر می‌خواند. این کار دستی است، پس ممکن است کمی طول بکشد.",
      three: "بعد از تأیید، با همین شماره وارد می‌شوید و کلاس‌تان آماده است.",
    },

    formTitle: "مشخصات شما",
    firstName: "نام",
    lastName: "نام خانوادگی",
    nationalId: "کد ملی",
    /** Why it is asked for, since a national id is not something anyone gives without a reason. */
    nationalIdNote: "برای اینکه بدانیم درخواست از طرف چه کسی است.",
    badNationalId: "این کد ملی معتبر نیست. رقم‌ها را یک‌بار دیگر ببینید.",
    /**
     * Two buttons rather than a dropdown, and a plain label.
     *
     * A select on a phone opens a wheel for a choice with two answers, which is more machinery than
     * the question deserves. Nothing in the product reads this — no game and no goal changes because
     * of it — so it is asked once, plainly, and never mentioned again.
     */
    gender: "جنسیت",
    genderFemale: "زن",
    genderMale: "مرد",
    schoolName: "نام پیش‌دبستانی",
    schoolNamePlaceholder: "مثلاً پیش‌دبستانی مهر",
    phone: "شماره موبایل",
    phoneNote: "با همین شماره وارد پنل می‌شوید.",
    /** One button, because the form is one page: it sends the code and the code sends the form. */
    next: "فرستادن کد تأیید",
    codeIntro: "کد را وارد کنید تا درخواست ثبت شود.",
    editDetails: "برگشتن به فرم",
    submit: "فرستادن درخواست",
    alreadyApplied: "با این شماره قبلاً درخواست داده‌اید و منتظر بررسی است.",
    nationalIdTaken: "با این کد ملی درخواستی ثبت شده است.",
    haveAccount: "قبلاً تأیید شده‌ام، وارد می‌شوم",
    done: {
      title: "درخواست شما ثبت شد",
      body: "بعد از بررسی، کلاس شما باز می‌شود. بعد با همین شماره وارد شوید تا شاگردهایتان را ثبت کنید.",
      enter: "رفتن به صفحه‌ی ورود",
    },
  },

  /** The teacher's surface. */
  teacher: {
    failed: "مشکلی پیش آمد: {message}",
    loading: "در حال بارگذاری…",
    signOut: "خروج از حساب",
    /**
     * The teacher's own page.
     *
     * Nothing here is editable: the name and the school came from an application somebody read, and
     * the phone number is the credential itself.
     */
    profile: {
      title: "حساب من",
      back: "بازگشت به کلاس",
      open: "حساب من",
      classes: "تعداد کلاس‌ها",
      students: "همه‌ی شاگردها",
      edit: "ویرایش اطلاعات",
      save: "ثبت",
      cancel: "بی‌خیال",
      saved: "ثبت شد.",
      /** Two fields are shown and not editable, each with the reason beside it. */
      phoneLabel: "شماره موبایل",
      phoneNote: "شماره‌ی ورود شماست و از این‌جا عوض نمی‌شود.",
      schoolLabel: "پیش‌دبستانی",
      schoolNote: "ثبت‌نام شما با همین نام تأیید شده است.",
      signOutNote: "شاگردی که روی این دستگاه وارد شده، وارد می‌ماند.",
    },
    roster: {
      title: "کلاس من",
      greeting: "سلام {name}",
      empty: "هنوز شاگردی ثبت نکرده‌اید.",
      emptyHint: "با دکمه‌ی زیر اولین شاگرد را اضافه کنید.",
      add: "افزودن شاگرد",
      count: "{count} شاگرد",
      lastPlayed: "آخرین بازی: {when}",
      neverPlayed: "هنوز بازی نکرده",
      mastered: "{count} هدف یاد گرفته",
      nextUp: "بعدی: {activity}",
      reasonAssigned: "چون شما خواسته‌اید",
      reasonUnfinished: "نیمه‌کاره مانده",
      reasonProgression: "قبلی را یاد گرفت",
      reasonStarting: "شروع",
      reasonCaughtUp: "فعلاً همه‌چیز را یاد گرفته",
    },
    /**
     * Classes — the switcher, and making one.
     *
     * A teacher with a morning and an afternoon group has two sets of children who must not share a
     * roster or a median, so the class is picked before anything else is read (ADR-012). With one class
     * the switcher is a single name and says so quietly; it only becomes a control when there are two.
     */
    classes: {
      title: "کلاس‌های من",
      intro: "کلاس فعال را انتخاب کنید، یا کلاس تازه بسازید.",
      back: "بازگشت به کلاس",
      manage: "مدیریت کلاس‌ها",
      /**
       * Children whose class was closed.
       *
       * Said as waiting rather than as lost: nothing of theirs was deleted, and the only thing they
       * need is a class to be in.
       */
      unplacedTitle: "شاگردهای بی‌کلاس",
      unplacedNote: "کلاس این شاگردها بسته شده. با انتخاب یک کلاس، دوباره سر جایشان می‌نشینند.",
      unplacedFrom: "قبلاً در {name}",
      placeHere: "افزودن به {name}",
      add: "کلاس تازه",
      addTitle: "کلاس تازه",
      name: "نام کلاس",
      namePlaceholder: "مثلاً کلاس صبح",
      save: "ثبت",
      cancel: "بی‌خیال",
      rename: "تغییر نام کلاس",
      renameTitle: "نام کلاس",
      nameTaken: "کلاس دیگری با همین نام دارید.",
      close: "بستن کلاس",
      closeTitle: "بستن کلاس",
      /** What closing does and does not do, before it is offered. */
      closeWarning:
        "این کلاس و شاگردهایش از فهرست شما بیرون می‌روند. کارهایی که انجام داده‌اند پاک نمی‌شود، ولی دیگر آن را نمی‌بینید. این کار برگشت‌پذیر نیست.",
      closeConfirm: "بله، ببند",
      /** Refused rather than done: a teacher with no class has no screen that works. */
      lastClass: "این تنها کلاس شماست و نمی‌شود بستش. اول یک کلاس دیگر بسازید.",
      count: "{count} شاگرد",
      /**
       * The class allowance, when it is full.
       *
       * Shown in place of the "new class" button rather than beside it, for the reason the close button
       * disappears on a teacher's last class: a control whose only outcome is a refusal is worse than no
       * control. It names the number and says who to ask, because this is the one refusal on the screen
       * a teacher cannot resolve themselves — retyping the name will never help.
       *
       * It also says the way out that IS theirs: a class they no longer teach can be closed, and that
       * frees the room. Without that line the only reading is "wait for somebody else".
       */
      limitFull: "به تعداد کلاس‌های مجازتان رسیده‌اید: {limit} کلاس. می‌توانید کلاسی را که دیگر ندارید ببندید، یا برای بیشتر با مدیر پیش‌دبستانی حرف بزنید.",
    },
    addStudent: {
      title: "شاگرد تازه",
      firstName: "نام",
      lastName: "نام خانوادگی",
      phone: "شماره موبایل",
      phoneNote:
        "شاگرد با همین شماره وارد بازی می‌شود. اگر بچه گوشی ندارد، شماره‌ی خانواده‌اش را بنویسید.",
      save: "ثبت",
      cancel: "بی‌خیال",
      taken: "این شماره قبلاً ثبت شده است.",
      badPhone: "این شماره‌ی موبایل درست به نظر نمی‌رسد.",
      /**
       * The student allowance, when it is full.
       *
       * Says that the count is over ALL their classes, because the roster in front of them shows one and
       * the number will not match it — a teacher with eleven children on screen being told they have
       * twenty would otherwise read it as a fault in the product. Children in a class they have closed
       * are counted too, and removing a child gives their place back.
       */
      limitFull: "به تعداد شاگردهای مجازتان رسیده‌اید: {limit} شاگرد در همه‌ی کلاس‌هایتان با هم، حتی کلاس‌های بسته. برای شاگرد تازه، یا شاگردی را حذف کنید یا با مدیر پیش‌دبستانی حرف بزنید.",
      /** Moves to the confirm step, not to the server yet — nothing is saved until that screen. */
      next: "ادامه",
      confirmTitle: "بررسی قبل از ثبت",
      /**
       * Why there is a second screen at all: a mistyped digit here has sent the sign-in SMS to a
       * stranger before, and the fix has to happen before the request goes out, not after.
       */
      confirmWarning:
        "پیامک ورود دقیقاً به همین شماره فرستاده می‌شود. لطفاً قبل از ثبت، شماره را با دقت بررسی کنید.",
      confirmNameLabel: "نام و نام خانوادگی",
      confirmSave: "تایید و ثبت",
      edit: "ویرایش",
    },
    /**
     * Which games the class may play (ADR-014).
     *
     * Shut by default, and the teacher opens them. Said as a decision they make rather than as a
     * restriction the product imposes, because it is one.
     */
    games: {
      title: "بازی‌های کلاس",
      back: "بازگشت به کلاس",
      intro: "هر بازی را که خواستید برای کل کلاس باز کنید. بازی‌های بسته در صفحه‌ی شاگرد دیده نمی‌شوند.",
      open: "باز",
      closed: "بسته",
      openCount: "{count} بازی باز است",
      noneOpen: "هنوز هیچ بازی‌ای باز نکرده‌اید.",
      /**
       * The games, grouped by the area of the week they belong to.
       *
       * The same seven words the assign screen and the class grid already use. A teacher planning a
       * week thinks "we are on counting", and the answer to "which games help with that" should be
       * a heading rather than a hunt down a list of twenty-five.
       */
      strandOpenAll: "باز کردن همه",
      strandCloseAll: "بستن همه",
      strandOpenSome: "{open} از {total} باز",
      /** How much of the week a game carries. Fifteen goals and two are not the same offer. */
      goalCount: "{count} هدف",
    },

    /**
     * The games, as a teacher sees them.
     *
     * A teacher opening a game here plays it for real and nothing is recorded — a teacher's session
     * cannot append events (R-3), because a teacher is not the instrument. The screen carried two lines
     * explaining that and they were removed at the founder's request: the screen is reached from a
     * button on the teacher's own roster, so what it is needs no stating, and there is no student
     * selected for a run to be attributed to.
     */
    preview: {
      title: "بازی‌ها",
      back: "بازگشت به کلاس",
      /** The end of a preview run. No score: nothing here was recorded, and a teacher is not measured. */
      finished: "این بازی تمام شد.",
      again: "یک‌بار دیگر",
      /**
       * One line, because its absence cost a real misunderstanding.
       *
       * Two paragraphs saying this were removed at the founder's request as over-explaining — and then
       * the same person played several games here and asked why none of it appeared in a child's
       * record. The answer is that a teacher's session cannot write events (R-3); what was missing was
       * anyone saying so. One caption, where the games are, is the smallest honest version.
       */
      nothingRecorded: "بازی کردن شما ثبت نمی‌شود. برای ثبت، شاگرد باید با شماره‌ی خودش وارد شود.",
    },

    /** One student, in full. */
    student: {
      back: "بازگشت به کلاس",
      neverPlayed: "هنوز بازی نکرده است.",
      neverPlayedHint: "وقتی با شماره‌ی خودش وارد شود و بازی کند، اینجا پر می‌شود.",
      /** The three numbers a teacher reads first. Labels only — the values are counts. */
      statSessions: "نشست",
      statDays: "روز",
      statMastered: "یادگرفته",
      /** The strand bars: what each section of the week has covered so far. */
      strandsHeading: "هر بخش تا کجا رفته",
      strandCount: "{done} از {total}",
      /**
       * The suggestion, promoted from a sentence to the one coloured thing on the screen.
       *
       * `nextUp` stays for the roster, which has no room for a card. Here the goal is named on its
       * own line and the game it is practised in sits under it, because a teacher deciding whether
       * to hand it over wants both.
       */
      nextLabel: "کار بعدی",
      nextIn: "در «{game}» · مرحله‌ی {level}",
      nextGive: "این را به {name} بده",
      nextGiven: "داده شد",
      nextNothing: "همه‌ی هدف‌های در دسترس را یاد گرفته.",
      /**
       * An objective with a lot of trials behind it and no run yet.
       *
       * **It describes the RECORD, never the child.** "۲۴ نوبت، هنوز پشت‌سرهم نشده" is a fact about
       * what happened; anything of the shape "is weak at this" is a judgement and does not belong on
       * a screen about a five-year-old (v2 §19).
       */
      stuckNote: "{count} نوبت، هنوز {need} تای پشت‌سرهم نشده",
      notStarted: "شروع نشده",
      mastered: "یاد گرفته",
      trials: "{count} نوبت",
      gamesHeading: "در هر بازی کجاست",
      level: "مرحله‌ی {reached} از {max}",
      accuracy: "{correct} از {total} درست",
      assignedByYou: "شما خواسته‌اید",
      nextUp: "بعدی: {activity}",
      giveGoal: "دادن هدف تازه",
      giveGoalTo: "دادن این هدف به {name}",
      editTitle: "ویرایش نام",
      edit: "ویرایش",
      save: "ثبت",
      cancel: "بی‌خیال",
      removeTitle: "حذف از کلاس",
      remove: "حذف از کلاس",
      /**
       * A destructive action, said in full before it is offered.
       *
       * What is erased and what is kept are different things, and a teacher pressing this deserves to
       * know which is which — the child's name goes, the record of what they practised stays.
       */
      removeWarning:
        "نام و شماره‌ی این شاگرد پاک می‌شود و از کلاس بیرون می‌رود. کارهایی که انجام داده در تاریخ کلاس می‌ماند، ولی به نام او نه. این کار برگشت‌پذیر نیست.",
      removeConfirm: "بله، حذف کن",
    },

    /**
     * The objective grid, and where goals are handed out.
     *
     * **Rebuilt to stop growing with the class.** The first version drew one chip per student per
     * objective — forty-three objectives times twenty children is eight hundred and sixty chips, on a
     * page a teacher has to scroll past to reach anything. Now a strand is one collapsed row until it
     * is opened, an objective is one line with a count, and names appear only for the one objective a
     * teacher taps. Nothing on the first screen scales with how many children are in the class.
     */
    goals: {
      title: "اهداف کلاس",
      back: "بازگشت به کلاس",
      /**
       * What this screen is, in one line.
       *
       * Without it the page opens on six unlabelled rows of category names and a teacher has to work
       * out from the buttons what any of it does.
       */
      intro: "هدف را به کلاس می‌دهید؛ بازی مربوط به آن برای شاگردها باز می‌شود.",
      /**
       * The three sections, in the order a teacher needs them.
       *
       * The screen used to open on the catalogue, so the thing they came to do — hand out this
       * week's goal — sat under two taps and seventy-nine names. It opens on what is already
       * running, offers the next step in each area, and keeps the catalogue folded underneath for
       * anyone who wants to go out of order.
       */
      liveHeading: "الان روی این‌ها کار می‌کنید — {count} هدف",
      noneLive: "هنوز هدفی به کلاس نداده‌اید.",
      noneLiveHint: "از «قدم بعدیِ هر بخش» پایین یکی را بدهید.",
      nextHeading: "قدم بعدیِ هر بخش",
      /**
       * How many children have not finished the objective being offered.
       *
       * The one number that belongs beside a suggestion, and it describes the class rather than
       * recommending anything about a child: it is a count of who has not got there yet.
       */
      nextRemaining: "{count} نفر هنوز یاد نگرفته‌اند",
      nextStrandDone: "همه‌ی هدف‌های این بخش را یاد گرفته‌اند.",
      allHeading: "همه‌ی هدف‌ها",
      /**
       * The class on one objective, as three states rather than one number.
       *
       * A bar with three parts, because there are three states and no fourth. A percentage would be
       * a number with no referent — there is no sixtieth of an idea — and it would also hide the
       * distinction the teacher acts on: a child who has not started and a child who is stuck are
       * different problems.
       */
      barLearnt: "{count} یاد گرفتند",
      barPractising: "{count} در حال تمرین",
      barNotStarted: "{count} شروع نکرده",
      /** On the row itself: what the teacher did, kept apart from what the children did. */
      empty: "برای دیدن این صفحه اول باید شاگردی ثبت کنید.",
      /**
       * What the strand row counts, said in the row.
       *
       * It read "{done} از {total} هدف" — nought of fifteen — directly above rows that said a goal HAD
       * been given, so the two contradicted each other. They were counting different things: the row
       * counts what the class has finished, and finishing takes weeks. Now it says which.
       */
      strandSummary: "{done} از {total} یاد گرفته",
      /** How many of this strand's objectives are live right now. Only shown when any are. */
      assign: "دادن به کلاس",
      withdraw: "برداشتن",
      /**
       * Clearing the board, said in full before it is offered.
       *
       * A whole class's goals at once, so it gets the same two-step shape as removing a student: what
       * happens and what does not, then the button. The second sentence is the one that matters — a
       * teacher's real fear here is losing what the children have done, and they have not.
       */
      resetTitle: "برداشتن همه‌ی هدف‌ها",
      reset: "برداشتن همه‌ی هدف‌ها",
      resetWarning:
        "همه‌ی هدف‌هایی که به این کلاس داده‌اید برداشته می‌شود، مثل اینکه یکی‌یکی «برداشتن» را زده باشید. کارهایی که بچه‌ها انجام داده‌اند و چیزهایی که یاد گرفته‌اند دست‌نخورده می‌ماند، و بازی‌ها هم باز می‌مانند.",
      resetConfirm: "بله، همه را بردار",
      resetCancel: "بی‌خیال",
      resetDone: "{count} هدف برداشته شد.",
      resetNothing: "هدفی برای برداشتن نبود.",
      /**
       * Said once, at the top, because it is the thing a teacher will otherwise assume the opposite of
       * — and a product that silently refused an early assignment would lose their trust the first time
       * it happened.
       */
      orderNote: "ترتیب پیشنهادی است، نه قفل. هر هدفی را هر وقت خواستید می‌توانید بدهید.",
    },

    /** Students against each other, inside this class and nowhere else. */
    compare: {
      title: "مقایسه‌ی کلاس",
      back: "بازگشت به کلاس",
      empty: "هنوز داده‌ی کافی برای مقایسه نیست.",
      emptyHint: "وقتی چند شاگرد چند نوبت بازی کنند، این جدول پر می‌شود.",
      thinData: "نوبت کم",
      /** The grid, which replaced one list per game. */
      gridHeading: "همه‌ی کلاس، همه‌ی بازی‌ها",
      gridMedian: "میانه {level}",
      gridNoMedian: "—",
      needsHelpCount: "{count} نفر",
      /**
       * The legend, and it is not decoration.
       *
       * A tinted cell makes a claim, so the claim has to be written down somewhere on the screen.
       * Three of these five say something about a child's level; the other two say why a cell is
       * empty, which is the distinction a teacher otherwise reads as failure.
       */
      keyAbove: "بالاتر از میانه‌ی کلاس",
      keyAt: "نزدیک میانه",
      keyBelow: "پایین‌تر از میانه",
      keyThin: "نوبت کم",
      keyNone: "بازی نکرده",
      /**
       * Closing the per-game list.
       *
       * There is no matching "open" string: the column head IS the control, and a label next to it
       * would be a second name for the same game on a row that has room for one word.
       */
      closeGame: "بستن",
      needsHelpHeading: "کسانی که به کمک نیاز دارند",
      needsHelpEmpty: "کسی به‌طور معنادار از کلاس عقب نیست.",
      needsHelpRow: "{name} در «{game}»: مرحله‌ی {reached}، میانه‌ی کلاس {median}",
      /**
       * The floor, stated on the screen rather than buried in the code.
       *
       * Every ladder starts everyone at the bottom, so one visit is indistinguishable from
       * struggling. A teacher who cannot see why a child is absent from a column will read the gap
       * as the child having failed.
       */
      floorNote:
        "شاگردی که کمتر از {min} نوبت در یک بازی داشته، در میانه و در فهرست پایین حساب نمی‌شود — یک‌بار بازی کردن با عقب بودن یکی نیست.",
      /** The scope qualifier. Not optional, and not shortenable. */
      scopeNote:
        "این مقایسه فقط میان شاگردان همین کلاس و بر پایه‌ی همین بازی‌هاست. هیچ مقایسه‌ای با کودکان بیرون از این کلاس در آن نیست.",
    },
  },
} as const);
